<script setup lang="ts">
import AppLayout from '../../layouts/AppLayout.vue'
import { ref, computed, onMounted } from 'vue'
import { useAuth } from '../../composables/useAuth'
import { getAdvisorTrainingAssets } from '../../services/api'
import { useRouter } from 'vue-router'
import SupplierLogo from '../../components/SupplierLogo.vue'
import { useCurrentContext } from '../../composables/useCurrentContext'
import type { AdvisorTrainingAssetListItem } from '../../types/api'

const { advisorId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()
const router = useRouter()
const loading = ref(true)
const assets = ref<AdvisorTrainingAssetListItem[]>([])
const allAssets = ref<AdvisorTrainingAssetListItem[]>([])
const search = ref('')
const selectedType = ref('')
const selectedSkillLevel = ref('')
const selectedSupplier = ref('')
const selectedTsd = ref('')
const page = ref(1)
const perPage = 24

const assetTypes = ['Video', 'PDF', 'Deck', 'Webinar']
const skillLevels = [
    { value: 'BEGINNER', label: 'Beginner' },
    { value: 'INTERMEDIATE', label: 'Intermediate' },
    { value: 'ADVANCED', label: 'Advanced' },
]

const tsdOptions = [
    'Telarus',
    'Avant',
    'Sandler Partners',
    'Intelisys',
    'Bridgepointe Technologies',
    'AppDirect',
]

// Favorites: stored in localStorage keyed by supplier ID
const FAVORITES_KEY = 'training-favorite-suppliers'
const favoriteSupplierIds = ref<Set<string>>(new Set(
    JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]')
))

const toggleFavorite = (supplierId: string) => {
    const next = new Set(favoriteSupplierIds.value)
    if (next.has(supplierId)) {
        next.delete(supplierId)
    } else {
        next.add(supplierId)
    }
    favoriteSupplierIds.value = next
    localStorage.setItem(FAVORITES_KEY, JSON.stringify([...next]))
}

const isFavorite = (supplierId: string) => favoriteSupplierIds.value.has(supplierId)

const supplierOptions = computed(() => {
    const seen = new Map<string, { id: string; name: string }>()
    for (const a of allAssets.value) {
        if (a.supplier && !seen.has(a.supplier.id)) {
            seen.set(a.supplier.id, { id: a.supplier.id, name: a.supplier.name })
        }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
})

// Sort assets: favorites first, then strip leading non-alphanumeric for locale sort
const sortedAssets = computed(() => {
    const filtered = selectedTsd.value
        ? assets.value.filter(a => a.supplierTsds?.includes(selectedTsd.value))
        : assets.value
    const sorted = [...filtered]
    sorted.sort((a, b) => {
        // Favorites first
        const aFav = favoriteSupplierIds.value.has(a.supplier.id) ? 0 : 1
        const bFav = favoriteSupplierIds.value.has(b.supplier.id) ? 0 : 1
        if (aFav !== bFav) return aFav - bFav

        // Strip leading special characters for clean alpha sort
        const nameA = (a.supplier.name || a.title || '').replace(/^[^a-zA-Z0-9]+/, '')
        const nameB = (b.supplier.name || b.title || '').replace(/^[^a-zA-Z0-9]+/, '')
        return nameA.localeCompare(nameB, 'en', { sensitivity: 'base' })
    })
    return sorted
})

const paginatedAssets = computed(() => {
    const start = (page.value - 1) * perPage
    return sortedAssets.value.slice(start, start + perPage)
})

const totalPages = computed(() => Math.ceil(sortedAssets.value.length / perPage))

let timeout: ReturnType<typeof setTimeout> | undefined
const handleSearch = () => {
  loading.value = true
  page.value = 1
  clearTimeout(timeout)
  timeout = setTimeout(() => {
    fetchAssets()
  }, 300)
}

const fetchAssets = async () => {
    try {
        loading.value = true
        const token = await getAccessTokenSilently()
        assets.value = await getAdvisorTrainingAssets(advisorId.value, {
            q: search.value || undefined,
            type: selectedType.value || undefined,
            skillLevel: selectedSkillLevel.value || undefined,
            supplierId: selectedSupplier.value || undefined,
        }, token)
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

const fetchAllForFilters = async () => {
    try {
        const token = await getAccessTokenSilently()
        allAssets.value = await getAdvisorTrainingAssets(advisorId.value, {}, token)
    } catch (e) {
        console.error(e)
    }
}

const clearFilters = () => {
    search.value = ''
    selectedType.value = ''
    selectedSkillLevel.value = ''
    selectedSupplier.value = ''
    selectedTsd.value = ''
    page.value = 1
    fetchAssets()
}

const hasActiveFilters = computed(() => {
    return search.value || selectedType.value || selectedSkillLevel.value || selectedSupplier.value || selectedTsd.value
})

onMounted(() => {
    fetchAssets()
    fetchAllForFilters()
})

const getIconType = (type: string) => {
    if (type === 'Video') return 'video'
    if (type === 'PDF') return 'pdf'
    return 'graduation'
}

const getSkillBadge = (level?: string) => {
    switch (level) {
        case 'BEGINNER': return { label: 'Beginner', class: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
        case 'INTERMEDIATE': return { label: 'Intermediate', class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' }
        case 'ADVANCED': return { label: 'Advanced', class: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' }
        default: return null
    }
}
</script>

<template>
  <AppLayout>
    <div class="space-y-6">
      <div class="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
           <h1 class="text-2xl font-bold text-cc-text">Training Center</h1>
           <p class="text-cc-text-secondary">Master the portfolio with enablement content from every supplier.</p>
        </div>
        <div class="flex flex-wrap gap-3 w-full md:w-auto">
             <select v-model="selectedTsd" @change="page = 1" class="border border-cc-border-med rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none bg-cc-elevated text-cc-text">
                 <option value="">All TSDs</option>
                 <option v-for="t in tsdOptions" :key="t" :value="t">{{ t }}</option>
             </select>
             <select v-model="selectedType" @change="page = 1; fetchAssets()" class="border border-cc-border-med rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none bg-cc-elevated text-cc-text">
                 <option value="">All Types</option>
                 <option v-for="t in assetTypes" :key="t" :value="t">{{ t }}</option>
             </select>
             <select v-model="selectedSkillLevel" @change="page = 1; fetchAssets()" class="border border-cc-border-med rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none bg-cc-elevated text-cc-text">
                 <option value="">All Levels</option>
                 <option v-for="s in skillLevels" :key="s.value" :value="s.value">{{ s.label }}</option>
             </select>
             <select v-model="selectedSupplier" @change="page = 1; fetchAssets()" class="border border-cc-border-med rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none bg-cc-elevated text-cc-text">
                 <option value="">All Suppliers</option>
                 <option v-for="s in supplierOptions" :key="s.id" :value="s.id">{{ s.name }}</option>
             </select>
             <div class="relative flex-1 md:w-64">
                <input v-model="search" @input="handleSearch" type="text" placeholder="Search topics..." class="w-full border border-cc-border-med bg-cc-elevated text-cc-text rounded-lg pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none shadow-sm" />
                <span class="absolute left-3 top-2.5 text-gray-400"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg></span>
             </div>
             <button v-if="hasActiveFilters" @click="clearFilters" class="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 px-2 py-2.5 whitespace-nowrap">
                 Clear All
             </button>
        </div>
      </div>

      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
         <div v-for="i in 6" :key="i" class="bg-gray-100 dark:bg-gray-700 h-40 rounded-lg"></div>
      </div>

      <div v-else-if="assets.length === 0" class="text-center py-20 bg-cc-elevated rounded-lg border border-dashed border-cc-border">
         <div class="text-gray-400 dark:text-gray-500 mb-2">No training found</div>
         <p class="text-sm text-cc-text-secondary">Try clearing filters.</p>
      </div>

      <div v-else class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="asset in paginatedAssets" :key="asset.id" class="hover:bg-cc-hover transition bg-cc-elevated rounded-xl border border-cc-border p-5 flex flex-col justify-between">
            <div>
                <div class="flex justify-between items-start mb-3">
                    <span class="text-2xl">
                        <svg v-if="getIconType(asset.assetType) === 'video'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        <svg v-else-if="getIconType(asset.assetType) === 'pdf'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                        <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"/></svg>
                    </span>
                    <div class="flex items-center gap-1.5">
                        <span v-if="getSkillBadge(asset.skillLevel)" :class="getSkillBadge(asset.skillLevel)!.class" class="text-[10px] uppercase font-bold px-2 py-1 rounded">
                            {{ getSkillBadge(asset.skillLevel)!.label }}
                        </span>
                        <span class="text-[10px] uppercase font-bold px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-cc-text-secondary">{{ asset.assetType }}</span>
                    </div>
                </div>

                <h3 class="font-bold text-cc-text mb-2 leading-snug">{{ asset.title }}</h3>
                <p class="text-sm text-cc-text-secondary mb-4 line-clamp-3" v-if="asset.summary">{{ asset.summary }}</p>

                <div class="flex items-center gap-2 mb-4">
                    <div class="flex items-center gap-2 cursor-pointer flex-1 min-w-0" @click.stop="router.push(`/advisor/suppliers/${asset.supplier.id}`)">
                        <SupplierLogo :src="asset.supplier?.logoUrl" class="w-5 h-5 rounded-full bg-gray-50 dark:bg-gray-700 object-cover border border-gray-100 dark:border-gray-600 flex-shrink-0"/>
                        <span class="text-xs text-cc-text-secondary font-medium hover:text-indigo-600 dark:hover:text-indigo-400 truncate">{{ asset.supplier.name }}</span>
                    </div>
                    <button
                        @click.stop="toggleFavorite(asset.supplier.id)"
                        class="flex-shrink-0 p-1 rounded hover:bg-cc-hover transition-colors"
                        :title="isFavorite(asset.supplier.id) ? 'Remove from favorites' : 'Add to favorites'"
                    >
                        <svg class="w-4 h-4" :class="isFavorite(asset.supplier.id) ? 'text-amber-500 fill-amber-500' : 'text-gray-300 dark:text-gray-600'" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" fill="none">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                    </button>
                </div>
            </div>

            <div>
                <div class="min-h-[20px] text-xs text-gray-400 dark:text-gray-500 mb-3 truncate">
                    <span v-if="asset.products.length">For: {{ asset.products.map(p => p.name).join(', ') }}</span>
                </div>

                <a :href="asset.url" target="_blank" class="block w-full py-2 text-center text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-lg transition">
                    Open Resource
                </a>
            </div>
        </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="flex items-center justify-between pt-4 border-t border-cc-border">
            <div class="text-sm text-cc-text-secondary">
                Showing <span class="font-medium">{{ (page - 1) * perPage + 1 }}</span> to <span class="font-medium">{{ Math.min(page * perPage, sortedAssets.length) }}</span> of <span class="font-medium">{{ sortedAssets.length }}</span> resources
            </div>
            <div class="flex gap-2">
                <button @click="page > 1 && page--" :disabled="page === 1" class="px-3 py-1 border border-cc-border-med rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-white">Previous</button>
                <button @click="page < totalPages && page++" :disabled="page >= totalPages" class="px-3 py-1 border border-cc-border-med rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-white">Next</button>
            </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
